import { useCallback, useEffect, useState } from 'react';
import { useDispatch } from 'react-redux';
import { useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import { object } from 'yup';
import { UiActions } from 'modules/ui';
import { AccountActions } from 'modules/account';
import { NO_SELECT_NUMBER } from 'constants/form';
import apiInnovator from 'external/api/innovator';
import { RegisterProfileFormNewGraduateSecondValues } from 'types/profile';
import RequestError from 'classes/RequestError';
import { UpdateInnovatorRequest } from 'proto/v1/innovatorservice/innovatorservice';
import { MAX_PREFERRED_WORK_LOCATIONS } from 'constants/profile';
import { range } from 'utils/array';
import { scrollLayoutBodyToTop } from 'utils/layout';
import {
  getDefaultPreferredWorkLocationJapanPrefectureIdsValue,
  getDefaultPreferredOccupationIdsValue,
  getDefaultDttScoreValue,
  getDefaultOutputsValue,
  getDefaultNewGraduateProgrammingExperiencesValue,
  getDefaultCertificationsValue,
  getDefaultNewGraduatePreferredBusinessTypesValue,
} from 'utils/profile/defaultFormValues';
import {
  getPreferredWorkLocationsValueToUpdate,
  getPreferredOccupationIdsValueToUpdate,
  getOutputsValueToUpdate,
  getCertificationsValueToUpdate,
  getNewGraduateProgrammingExpericenceValueToUpdate,
  getDttScoreToUpdate,
  getDttRankIdToUpdate,
  getNewGraduatePreferredWorkStyleIdToUpdate,
  getNewGraduatePreferredBusinessTypeIdsValueToUpdate,
} from 'utils/profile/formValuesToUpdate';
import {
  englishSkillIdSchema,
  preferredWorkLocationJapanPrefectureIdSchema,
  preferredOccupationsSchema,
  dttScoreSchema,
  dttRankIdSchema,
  newGraduateProgrammingExperiencesSchema,
  outputsSchema,
  newGraduatePreferredWorkStyleIdSchema,
  newGraduatePreferredBusinessTypesSchema,
} from 'utils/profile/schema';
import { Stepper } from 'hooks/useStepper';
import useErrorNotification from 'hooks/useErrorNotification';
import usePreferredOccupationsFormField from 'hooks/profile/usePreferredOccupationsFormField';
import useDttFormField from 'hooks/profile/useDttFormField';
import useNewGraduateProgrammingExperiencesFormField from 'hooks/profile/useNewGraduateProgrammingExperiencesFormField';
import useNewGraduatePreferredBusinessTypesFormField from 'hooks/profile/useNewGraduatePreferredBusinessTypesFormField';
import useFetchInnovatorForm from 'hooks/profile/useFetchInnovatorForm';
import { stepsNewGraduate } from '../../steps';

const schema = object().shape({
  englishSkillId: englishSkillIdSchema,
  preferredWorkLocationJapanPrefectureIds: preferredWorkLocationJapanPrefectureIdSchema,
  preferredOccupations: preferredOccupationsSchema,
  outputs: outputsSchema,
  dttScore: dttScoreSchema,
  dttRankId: dttRankIdSchema,
  newGraduate: object().shape({
    preferredWorkStyleId: newGraduatePreferredWorkStyleIdSchema,
    programmingExperiences: newGraduateProgrammingExperiencesSchema,
    preferredBusinessTypes: newGraduatePreferredBusinessTypesSchema,
  }),
});

const defaultValues: RegisterProfileFormNewGraduateSecondValues = {
  englishSkillId: NO_SELECT_NUMBER,
  preferredWorkLocationJapanPrefectureIds: range(
    0,
    MAX_PREFERRED_WORK_LOCATIONS - 1,
  ).map(() => NO_SELECT_NUMBER),
  preferredOccupations: [],
  certifications: [],
  dttRankId: NO_SELECT_NUMBER,
  dttScore: '',
  outputs: [],
  newGraduate: {
    programmingExperiences: [],
    preferredWorkStyleId: NO_SELECT_NUMBER,
    preferredBusinessTypes: [],
  },
};

const useRegisterProfileFormNewGraduateSecond = ({
  stepper,
}: {
  stepper: Stepper;
}) => {
  const dispatch = useDispatch();
  const { handleRequestError } = useErrorNotification();
  const { formItems, innovator, innovatorToUpdate } = useFetchInnovatorForm();

  const [isValidDefault, setIsValidDefault] = useState<boolean>(false);

  const formMethods = useForm<RegisterProfileFormNewGraduateSecondValues>({
    mode: 'all',
    resolver: yupResolver(schema),
    defaultValues,
  });

  const { reset } = formMethods;

  const { preferredOccupationsError } = usePreferredOccupationsFormField({
    formMethods,
  });

  const {
    newGraduateDttScoreError,
    newGraduateRequiredAllDttFieldsError,
  } = useDttFormField({
    formMethods,
  });

  const {
    newGraduateRequiredAllProgrammingExperienceFieldsErrors,
  } = useNewGraduateProgrammingExperiencesFormField({
    formMethods,
  });

  const {
    newGraduatePreferredBusinessTypesError,
  } = useNewGraduatePreferredBusinessTypesFormField({
    formMethods,
  });

  const handleClickPrevButton = useCallback(() => {
    scrollLayoutBodyToTop();
    stepper.handlePrev();
  }, [stepper]);

  const onSubmit = useCallback(
    async (values: RegisterProfileFormNewGraduateSecondValues) => {
      const valuesToUpdate: UpdateInnovatorRequest = {
        // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
        ...innovatorToUpdate!,
        englishSkillId: values.englishSkillId,
        certifications: getCertificationsValueToUpdate(values.certifications),
        preferredWorkLocations: getPreferredWorkLocationsValueToUpdate(
          values.preferredWorkLocationJapanPrefectureIds,
        ),
        preferredOccupationIds: getPreferredOccupationIdsValueToUpdate(
          values.preferredOccupations,
        ),
        dttScore: getDttScoreToUpdate(values.dttScore),
        dttRankId: getDttRankIdToUpdate(values.dttRankId),
        outputs: getOutputsValueToUpdate(values.outputs),
        newGraduate: {
          // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
          ...innovatorToUpdate!.newGraduate!,
          programmingExperiences: getNewGraduateProgrammingExpericenceValueToUpdate(
            values.newGraduate.programmingExperiences,
          ),
          preferredWorkStyleId: getNewGraduatePreferredWorkStyleIdToUpdate(
            values.newGraduate.preferredWorkStyleId,
          ),
          preferredBusinessTypeIds: getNewGraduatePreferredBusinessTypeIdsValueToUpdate(
            values.newGraduate.preferredBusinessTypes,
          ),
        },
      };
      let data;
      try {
        ({ data } = await apiInnovator.updateInnovator(valuesToUpdate));
      } catch (error: unknown) {
        if (error instanceof RequestError) {
          dispatch(UiActions.setLoading(false));
          handleRequestError(error);
        }
        return;
      }
      dispatch(AccountActions.setInnovator(data.innovator));
      dispatch(UiActions.setLoading(false));
      scrollLayoutBodyToTop();
      stepsNewGraduate.formSecond.completed = true;
      stepper.handleNext();
    },
    [dispatch, handleRequestError, innovatorToUpdate, stepper],
  );

  // Set default values by the fetched innovator
  useEffect(() => {
    if (!innovatorToUpdate || !innovator) return;

    const values: RegisterProfileFormNewGraduateSecondValues = {
      englishSkillId: innovatorToUpdate.englishSkillId,
      preferredWorkLocationJapanPrefectureIds: getDefaultPreferredWorkLocationJapanPrefectureIdsValue(
        innovatorToUpdate.preferredWorkLocations,
      ),
      preferredOccupations: getDefaultPreferredOccupationIdsValue(
        innovatorToUpdate.preferredOccupationIds,
      ),
      certifications: getDefaultCertificationsValue(
        innovatorToUpdate.certifications,
      ),
      dttScore: getDefaultDttScoreValue(innovatorToUpdate.dttScore),
      dttRankId: innovatorToUpdate.dttRankId ?? NO_SELECT_NUMBER,
      outputs: getDefaultOutputsValue(innovatorToUpdate.outputs),
      newGraduate: {
        programmingExperiences: getDefaultNewGraduateProgrammingExperiencesValue(
          innovatorToUpdate?.newGraduate?.programmingExperiences,
        ),
        preferredWorkStyleId:
          innovatorToUpdate.newGraduate?.preferredWorkStyleId ??
          NO_SELECT_NUMBER,
        preferredBusinessTypes: getDefaultNewGraduatePreferredBusinessTypesValue(
          innovatorToUpdate.newGraduate?.preferredBusinessTypeIds,
        ),
      },
    };

    setIsValidDefault(schema.isValidSync(values));

    reset(values);
  }, [formItems.countries, innovator, innovatorToUpdate, reset]);

  return {
    innovator,
    onSubmit,
    formMethods,
    formItems,
    newGraduateDttScoreError,
    newGraduateRequiredAllDttFieldsError,
    newGraduateRequiredAllProgrammingExperienceFieldsErrors,
    newGraduatePreferredBusinessTypesError,
    preferredOccupationsError,
    isValidDefault,
    handleClickPrevButton,
  };
};

export default useRegisterProfileFormNewGraduateSecond;
